import { MidiTrack, type MidiFile } from "../models/midi-file";
import { DrumMap, ReaperDrumMap } from "../models/drum-map";
import type { ArticulationAliases } from "../models/aliases";

export type MappingValidation = {
  sourceArticulationsWithoutAlias: string[];
  aliasesWithMissingDestination: string[];
  duplicateDestinationNotes: number[];
  invalidSourceMappings: string[];
};

export type ConversionSummary = {
  convertedNotes: number;
  unresolvedNotes: Array<{ note: number; articulation: string | null }>;
};

export class RemapDrumsUseCase {
  invoke({
    midiFile,
    from,
    to,
    aliases,
  }: {
    midiFile: MidiFile;
    from: DrumMap;
    to: DrumMap;
    aliases?: ArticulationAliases;
  }): MidiFile {
    if (from === to) {
      return midiFile;
    }

    const fromMap = ReaperDrumMap.fromName(from);
    const toMap = ReaperDrumMap.fromName(to);

    return this.remapFile({ midiFile, from: fromMap, to: toMap, aliases });
  }

  remapFile({
    midiFile,
    from,
    to,
    aliases,
  }: {
    midiFile: MidiFile;
    from: ReaperDrumMap;
    to: ReaperDrumMap;
    aliases?: ArticulationAliases;
  }): MidiFile {
    const drumTracks = midiFile.tracks.filter(
      (track) =>
        track.name.toLowerCase().includes("percussion") ||
        track.name.toLowerCase().includes("drum") ||
        track.channel === 9 ||
        track.channel === 0,
    );

    if (drumTracks.length === 0) {
      throw new Error("No drum tracks found in the MIDI file");
    }

    for (const track of drumTracks) {
      this.remapTrack({ track, from, to, aliases });
    }

    return midiFile;
  }

  remapTrack({
    track,
    from,
    to,
    aliases,
  }: {
    track: MidiTrack;
    from: ReaperDrumMap;
    to: ReaperDrumMap;
    aliases?: ArticulationAliases;
  }): MidiTrack {
    this.convertTrack({ track, from, to, aliases });
    return track;
  }

  validateMappings({ from, to, aliases }: {
    from: ReaperDrumMap;
    to: ReaperDrumMap;
    aliases: ArticulationAliases;
  }): MappingValidation {
    const sourceNames = new Set(from.noteToName.values());
    const destinationNames = new Set(to.noteToName.values());
    const noteNames = new Map<number, string[]>();
    const sourceNoteNames = new Map<number, string[]>();
    for (const [note, name] of from.entries) {
      sourceNoteNames.set(note, [...(sourceNoteNames.get(note) ?? []), name]);
    }
    for (const [note, name] of to.entries) {
      noteNames.set(note, [...(noteNames.get(note) ?? []), name]);
    }
    return {
      sourceArticulationsWithoutAlias: [...sourceNames].filter(
        (name) => !name.endsWith("(CC)") && !aliases[name] && !to.nameToNote.get(name),
      ),
      aliasesWithMissingDestination: Object.entries(aliases)
        .filter(([, destination]) => !destinationNames.has(destination))
        .map(([source, destination]) => `${source} -> ${destination}`),
      duplicateDestinationNotes: [...noteNames]
        .filter(([, names]) => new Set(names).size > 1)
        .map(([note]) => note),
      invalidSourceMappings: [
        ...from.invalidEntries,
        ...[...sourceNoteNames]
          .filter(([, names]) => new Set(names).size > 1)
          .map(([note, names]) => `${note}: conflicting names ${[...new Set(names)].join(", ")}`),
      ],
    };
  }

  convertTrack({ track, from, to, aliases = {} }: {
    track: MidiTrack;
    from: ReaperDrumMap;
    to: ReaperDrumMap;
    aliases?: ArticulationAliases;
  }): ConversionSummary {
    const summary: ConversionSummary = { convertedNotes: 0, unresolvedNotes: [] };
    let index = 0;
    for (const event of track.events) {
      index++;
      if (event.type === "noteOn" || event.type === "noteOff") {
        const note = event.noteNumber;
        const name = from.getName(note);
        if (!name) {
          console.warn(
            `[${track.name}]: No name found for note "${note}" at position ${index}/${track.events.length}`,
            event,
          );
          summary.unresolvedNotes.push({ note, articulation: null });
          continue;
        }

        const destinationName = aliases[name] ?? name;
        const newNote = to.getNote(destinationName);
        if (newNote === null) {
          console.warn(
            `[${track.name}]: No note found for name "${destinationName}" at position ${index}/${track.events.length}`,
            event,
          );
          summary.unresolvedNotes.push({ note, articulation: name });
          continue;
        }

        event.noteNumber = newNote;
        summary.convertedNotes++;
      }
    }
    return summary;
  }
}
